import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, WAREHOUSE_IMG, COMPANY } from "@/lib/constants";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await register({ ...form, email: form.email.trim().toLowerCase() });
    setBusy(false);
    if (r.ok) {
      toast.success("Account created. Welcome to Apex.");
      nav("/portal");
    } else {
      toast.error(r.error);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-6 lg:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <Link to="/login" className="mono-label hover:text-zinc-900">&larr; BACK TO SIGN IN</Link>
          <h1 className="mt-6 text-4xl font-bold tracking-tight">Create shipper account</h1>
          <p className="mt-2 text-zinc-600 text-sm">Submit jobs, set appointments, track loads.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mono-label">Full name</label>
                <Input required value={form.name} onChange={upd("name")} className="sharp mt-2 apex-input h-11" data-testid="reg-name" />
              </div>
              <div>
                <label className="mono-label">Company</label>
                <Input value={form.company} onChange={upd("company")} className="sharp mt-2 apex-input h-11" data-testid="reg-company" />
              </div>
            </div>
            <div>
              <label className="mono-label">Email</label>
              <Input required type="email" value={form.email} onChange={upd("email")} className="sharp mt-2 apex-input h-11" data-testid="reg-email" />
            </div>
            <div>
              <label className="mono-label">Phone</label>
              <Input value={form.phone} onChange={upd("phone")} className="sharp mt-2 apex-input h-11" data-testid="reg-phone" />
            </div>
            <div>
              <label className="mono-label">Password (min 6)</label>
              <Input required type="password" minLength={6} value={form.password} onChange={upd("password")}
                className="sharp mt-2 apex-input h-11" data-testid="reg-password" />
            </div>
            <Button type="submit" disabled={busy}
              className="sharp bg-[#0056B3] hover:bg-[#004494] w-full h-12" data-testid="reg-submit">
              {busy ? "Creating..." : "Create account"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          <div className="mt-6 text-sm text-zinc-600">
            Already have one? <Link to="/login" className="underline text-[#0056B3]">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={WAREHOUSE_IMG} alt="warehouse" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-zinc-950/50" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-zinc-100">
          <div className="flex items-center gap-3 justify-end">
            <div className="text-right">
              <div className="font-bold tracking-tight">{COMPANY.name}</div>
              <div className="mono-label !text-zinc-300">CUSTOMER PORTAL</div>
            </div>
            <img src={LOGO_URL} alt="Apex" className="h-10 w-10 bg-white p-1" />
          </div>
          <div>
            <div className="mono-label !text-zinc-300">FOR SHIPPERS</div>
            <p className="mt-3 text-3xl font-bold tracking-tight leading-tight max-w-md">
              Book jobs. Track loads. Approve BOLs.<br />From your desk or your phone.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
